import type {
    CellRendererSelectorFunc,
    ColDef,
    ColGroupDef,
    GridOptions,
    ICellRendererParams,
} from '@ag-grid-community/core';
import type { ReactNode } from 'react';
import { type ICSAgGridProps } from './ICSAgGrid';
import { ActionCellRenderer } from './action-cell-renderer';
import { CustomCell } from './CustomCell/CustomCell';

export const noGroupDisplayCellRenderer: CellRendererSelectorFunc = (
    params: ICellRendererParams,
) => {
    if (!params.node.group) {
        return;
    }
    return { component: 'agGroupCellRenderer' };
};

function getUniqueId(id: string, usedIds: string[]): string {
    let tmpId = id;
    while (usedIds.includes(tmpId)) {
        tmpId = `${tmpId}_`;
    }
    return tmpId;
}

function addColumnIds<T>(
    columns: (ColGroupDef | ColDef<T>)[],
    usedIds: string[] = [],
    prefix?: string,
): ColDef<T>[] {
    return columns.map((column) => {
        if ('children' in column) {
            const calculatedId = column.groupId ?? column.headerName ?? '_';
            const id = getUniqueId(calculatedId, usedIds);
            return {
                ...column,
                groupId: id,
                children: addColumnIds(column.children, usedIds, id),
            };
        }
        const { colId, field, headerName } = column;
        if (colId) {
            return column;
        }

        const calculatedId = field ?? headerName ?? `${prefix ?? ''}_`;

        const id = getUniqueId(calculatedId, usedIds);
        return { colId: id, ...column };
    });
}

function suppressFiltering<T>(
    columns: (ColGroupDef | ColDef<T>)[],
    columnTypes?: GridOptions['columnTypes'],
    showFloatingFilters?: boolean,
): ColDef<T>[] {
    const shouldShowFloatingFilters = Boolean(showFloatingFilters);

    const resolveFilter = (col: ColDef<T>): string | false => {
        if (col.filter === false) return false;
        if (typeof col.filter === 'string') {
            if (shouldShowFloatingFilters) {
                return col.filter;
            }

            return col.filter === 'agSetColumnFilter'
                ? 'agSetColumnFilter'
                : 'agTextColumnFilter';
        }
        // No explicit filter — check if the column type defines one
        const typeName = Array.isArray(col.type) ? col.type[0] : col.type;
        const typeFilter =
            typeName && columnTypes ? columnTypes[typeName].filter : undefined;
        if (shouldShowFloatingFilters && typeof typeFilter === 'string') {
            return typeFilter;
        }

        if (typeFilter === 'agSetColumnFilter') return 'agSetColumnFilter';
        if (typeof typeFilter === 'string') return typeFilter;
        // No filter from type either — default to text so setFilterModel works
        return col.filter ?? 'agTextColumnFilter';
    };

    const applyToLeaf = (child: ColDef<T>): ColDef<T> => ({
        ...child,
        suppressFiltersToolPanel: true,
        floatingFilter:
            shouldShowFloatingFilters && child.floatingFilter !== false,
        filter: resolveFilter(child),
        suppressFloatingFilterButton: true,
        suppressHeaderFilterButton: true,
    });

    return columns.map((column) => {
        if ('children' in column) {
            return {
                ...column,
                children: column.children.map(applyToLeaf),
            };
        }
        return applyToLeaf(column);
    });
}

export function addColumnGroupCollapsible<T>(
    columns: (ColGroupDef | ColDef<T>)[],
): (ColGroupDef | ColDef<T>)[] {
    return columns.map((column) => {
        const isColGroupDef =
            'children' in column && Array.isArray(column.children);

        if (
            !isColGroupDef ||
            column.children.length <= 2 ||
            !column.headerName
        ) {
            return {
                ...column,
                headerClass: isColGroupDef
                    ? 'ics-grid-group-header'
                    : undefined,
            };
        }
        const firstVisibleChildIndex = Math.max(
            0,
            column.children.findIndex(
                (child) => !('initialHide' in child && child.initialHide),
            ),
        );

        return {
            ...column,
            columnGroupShow: 'open',
            headerClass: 'ics-grid-group-header',
            openByDefault: true,
            children: column.children.map((child, index) => {
                if (
                    [
                        firstVisibleChildIndex,
                        firstVisibleChildIndex + 1,
                    ].includes(index)
                ) {
                    return { ...child, columnGroupShow: undefined };
                }
                return { ...child, columnGroupShow: 'open' };
            }, []),
        };
    });
}
type ColOrGroupDef<T> = ColDef<T> | ColGroupDef<T>;

function addExpandableColumn<T>(
    columns: ColOrGroupDef<T>[],
    { expandable, counter, expandColumnHeader }: ICSAgGridProps<T>,
): ColOrGroupDef<T>[] {
    const hasExpand = Boolean(expandable) || Boolean(counter);
    if (!hasExpand) {
        return columns;
    }

    const padding = 28;
    const customCellWidth =
        ((expandable ? 1 : 0) + (counter ? 1 : 0)) * 30 + padding;
    const hasGroups = columns.some((col) => 'children' in col);
    const expandableColumn = {
        colId: 'expandable',
        headerName: expandColumnHeader,
        initialWidth: customCellWidth,
        sortable: false,
        filter: false,
        floatingFilter: false,
        suppressAutoSize: false,
        suppressSizeToFit: false,
        pinned: 'left',
        resizable: false,
        lockVisible: true,
        suppressColumnsToolPanel: true,
        minWidth: customCellWidth,
        maxWidth: customCellWidth,
        cellRenderer: (params: ICellRendererParams<T>): ReactNode =>
            CustomCell({
                params,
                expandable,
                label: (params.getValue ? params.getValue() : '') as string,
                counter:
                    typeof counter === 'function' ? counter(params) : undefined,
            }),
    } as ColDef;
    const expandableColumnGroup: ColGroupDef<T> = {
        groupId: 'expandable_group',
        headerName: '',
        marryChildren: true,
        headerClass: expandColumnHeader ? 'hide-resize' : 'hide-resize',
        suppressColumnsToolPanel: true,
        children: [expandableColumn],
    };

    return [
        hasGroups ? expandableColumnGroup : expandableColumn,
        ...(columns ?? []),
    ];
}

function addActionColumn<T>(
    columns: ColOrGroupDef<T>[],
    props: ICSAgGridProps<T>,
): ColOrGroupDef<T>[] {
    if (!props.columnActionSettings) {
        return columns;
    }

    const actionColumn: ColDef<T> = {
        colId: 'actions',
        headerName: '',
        width: 120,
        minWidth: 120,
        maxWidth: 120,
        sortable: false,
        filter: false,
        floatingFilter: false,
        pinned: 'right',
        resizable: false,
        lockVisible: true,
        suppressColumnsToolPanel: true,
        cellRenderer: (params: ICellRendererParams<T>): ReactNode =>
            props.columnActionSettings?.customActionRenderer?.(params) ??
            ActionCellRenderer(params, props.columnActionSettings),
    };

    return [...columns, actionColumn];
}

export function addNeededColumns<T>(
    columns: ColDef<T>[],
    props: ICSAgGridProps<T>,
    columnTypes?: GridOptions['columnTypes'],
): ColDef<T>[] {
    let columnDefs = addColumnGroupCollapsible(columns);
    columnDefs = addExpandableColumn(columnDefs, props);
    columnDefs = addActionColumn(columnDefs, props);
    columnDefs = addColumnIds(columnDefs, []);
    columnDefs = suppressFiltering(
        columnDefs,
        columnTypes,
        props.showFloatingFilters,
    );
    return columnDefs;
}
